import { createSelector } from '@reduxjs/toolkit'
import { Application, EntityType, TradingEntity, AuthorisedUser } from 'api'
import { createWindowSelectionsSelector } from './selectionSelectors'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { createEntityGetterSelector } from './entitySelectors'

export const currentOrganisationIdSelector = createSelector(
  createAuthorisedUserSelector(),
  createWindowSelectionsSelector(),
  (authorisedUser, windowSelections) =>
    authorisedUser.application === Application.ADMIN_GATEWAY ? windowSelections.organisationId : authorisedUser.organisationId
)

export const currentOrganisationSelector = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => (currentOrganisationId ? getOrganisation(currentOrganisationId) : null)
)

export const isOwnOrganisationGetterSelector = createSelector(
  [currentOrganisationIdSelector],
  currentOrganisationId => (organisationId: string) => !currentOrganisationId || currentOrganisationId === organisationId
)

const tradingEntityBelongsToOrganisation = (tradingEntity: TradingEntity | null, organisationId: string | null) => {
  if (!tradingEntity) {
    return false
  } else {
    // admin users in global view will not have an organisation id and should see everything
    return !organisationId || organisationId === tradingEntity.organisationId
  }
}

const userBelongsToTradingEntity = (user: AuthorisedUser, tradingEntity: TradingEntity | null) => {
  if (!tradingEntity) {
    return false
  } else {
    return user.tradingEntityDetails.some(d => d.tradingEntityId === tradingEntity.id)
  }
}

// TODO JF rename to tradingEntityBelongsToUserOrganisationGetterSelector?  Name TBC
export const isOwnTradingEntityGetterSelector = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  (currentOrganisationId, getTradingEntity) => (tradingEntityId: string) => {
    const tradingEntity = getTradingEntity(tradingEntityId)
    return tradingEntityBelongsToOrganisation(tradingEntity, currentOrganisationId)
  }
)

export const isUserTradingEntityGetterSelector = createSelector(
  createAuthorisedUserSelector(),
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  (authorisedUser, currentOrganisationId, getTradingEntity) => (tradingEntityId: string) => {
    const tradingEntity = getTradingEntity(tradingEntityId)
    if (authorisedUser.application === Application.ADMIN_GATEWAY) {
      return tradingEntityBelongsToOrganisation(tradingEntity, currentOrganisationId)
    } else {
      return userBelongsToTradingEntity(authorisedUser, tradingEntity)
    }
  }
)
